import React, { useRef } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Breakpoint,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  TextFieldProps,
} from '@mui/material';

import { useT } from 'UI/lib';

export interface ConfirmationBasicDialogProps {
  titleText?: string;
  titleElements?: Record<string, React.ReactNode>;
  contentText?: string;
  contentElements?: Record<string, React.ReactNode>;
  cancelButtonText?: string;
  approveButtonText?: string;
  isOpen: boolean;
  handleClose: () => void;
  handleConfirm: () => void;
  handleReject: () => void;
  contentComponent?: React.ReactNode;
  maxWidth?: Breakpoint | false;
  fullWidth?: boolean;
  approveButtonColor?: TextFieldProps['color'];
}

const ConfirmationBasicDialog = ({
  titleText = 'common:confirmation',
  titleElements = {},
  contentText,
  contentElements = {},
  cancelButtonText = 'common:cancel',
  approveButtonText = 'common:confirm',
  isOpen,
  handleClose,
  handleConfirm,
  handleReject,
  contentComponent,
  maxWidth = 'xs',
  fullWidth,
  approveButtonColor = 'primary', //Currently this will only show one color anyway because our MUI theme only has primary buttons
}: ConfirmationBasicDialogProps) => {
  const t = useT();
  const isRejecting = useRef(false)
  const isConfirming = useRef(false)

  const rejectHandler = async () => {
    isRejecting.current = true
    try {
      await handleReject()
    } catch (error) {
      console.error(error, 'Rejection failed in dialog')
    } finally {
      isRejecting.current = false
    }
  }

  const confirmHandler = async () => {
    isConfirming.current = true
    try {
      await handleConfirm()
    } catch (error) {
      console.error(error, 'Confirmation failed in dialog')
    } finally {
      isConfirming.current = false
    }
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="confirmation-dialog-title"
      aria-describedby="confirmation-dialog-description"
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      PaperProps={{ 'data-test': 'dialog-confirmation' }}
    >
      <DialogTitle id="confirmation-dialog-title" sx={{ padding: '12px 48px 12px 20px' }}>
        {t(titleText, titleElements)}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
          data-test="dialog-confirmation-close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ padding: '40px 20px 20px' }}>
        {contentComponent ?? (
          <DialogContentText
            id="confirmation-dialog-description"
            data-test="confirmation-dialog-description"
          >
            {t(contentText, contentElements)}
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions sx={{ padding: 12 }}>
        <Button
          variant="text"
          onClick={rejectHandler}
          sx={{ textTransform: 'capitalize' }}
          data-test="confirmation-dialog-button-cancel"
          disabled={isRejecting.current || isConfirming.current}
        >
          {t(cancelButtonText)}
        </Button>
        <Button
          variant="contained"
          onClick={confirmHandler}
          autoFocus
          sx={{ textTransform: 'capitalize' }}
          data-test="confirmation-dialog-button-confirm"
          color={approveButtonColor}
          disabled={isRejecting.current || isConfirming.current}
        >
          {t(approveButtonText)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmationBasicDialog;
